import React, { useMemo, useState } from 'react';

const QUESTIONS = [
  {
    id: 1,
    prompt: 'Which data structure is primarily used by Breadth-First Search (BFS) to keep track of which node to visit next?',
    options: ['A. Stack', 'B. Queue', 'C. Priority Queue', 'D. Recursion'],
    correct: 'B. Queue',
    explanation:
      'BFS explores nodes level by level using a queue, where nodes are enqueued and dequeued in visit order.',
  },
  {
    id: 2,
    prompt: 'During Depth-First Search (DFS), what is an edge called when it connects a node to an already discovered ancestor?',
    options: ['A. Tree edge', 'B. Cross edge', 'C. Back edge', 'D. Forward edge'],
    correct: 'C. Back edge',
    explanation: 'In DFS, an edge that points back to an ancestor in the recursion stack is a back edge.',
  },
  {
    id: 3,
    prompt: 'What does it mean if an algorithm runs in O(n) time?',
    options: [
      'A. Its runtime is constant regardless of input size',
      'B. Its runtime grows proportionally with the input size',
      'C. Its runtime grows exponentially',
      'D. Its runtime depends on hardware speed',
    ],
    correct: 'B. Its runtime grows proportionally with the input size',
    explanation: 'O(n) describes linear growth with input size, such as scanning an unsorted array once.',
  },
];

const Quiz = ({ onPassed }) => {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const score = useMemo(() => {
    return QUESTIONS.reduce((count, question) => {
      return answers[question.id] === question.correct ? count + 1 : count;
    }, 0);
  }, [answers]);

  const allAnswered = QUESTIONS.every((q) => answers[q.id]);
  const passed = score === QUESTIONS.length;

  const handleSubmit = () => {
    if (!allAnswered) {
      alert('Please answer all questions first.');
      return;
    }
    setShowResults(true);
  };

  const handleTryAgain = () => {
    setShowResults(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#ffe5f1] to-[#ffd6ea] py-10 px-4 flex items-center justify-center">
      <div className="w-full max-w-3xl rounded-2xl bg-white/95 shadow-2xl border border-[#ffd3e6] p-6 md:p-10">
        <h1 className="text-3xl md:text-4xl font-semibold text-[#2a1a23]">you didn't think it would be that easy right?</h1>
        <p className="mt-2 text-[#6a4c5d]">Answer all 3 correctly to unlock the next stage.</p>

        <div className="mt-8 space-y-6">
          {QUESTIONS.map((question) => {
            const isCorrect = answers[question.id] === question.correct;
            const hasAnswer = Boolean(answers[question.id]);
            return (
              <section key={question.id} className="rounded-xl border border-[#f7d7e8] bg-[#fff7fb] p-5">
                <h2 className="text-lg font-medium text-[#2a1a23]">Question {question.id}</h2>
                <p className="mt-2 text-[#3f2d37]">{question.prompt}</p>

                <div className="mt-4 grid gap-3">
                  {question.options.map((option) => (
                    <label
                      key={option}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                        answers[question.id] === option
                          ? 'border-[#ff69a6] bg-[#ffe7f3]'
                          : 'border-[#f0d5e2] bg-white hover:bg-[#fff1f8]'
                      }`}
                    >
                      <input
                        type="radio"
                        name={`q-${question.id}`}
                        value={option}
                        checked={answers[question.id] === option}
                        onChange={() => setAnswers((prev) => ({ ...prev, [question.id]: option }))}
                        className="accent-[#ff4d97]"
                        disabled={showResults}
                      />
                      <span className="text-[#2e2028]">{option}</span>
                    </label>
                  ))}
                </div>

                {showResults && hasAnswer && (
                  <div className={`mt-4 rounded-lg p-3 text-sm ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-rose-50 text-rose-800'}`}>
                    <p className="font-semibold">{isCorrect ? 'Correct' : `Correct answer: ${question.correct}`}</p>
                    <p className="mt-1">{question.explanation}</p>
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {!showResults && (
            <button
              onClick={handleSubmit}
              className="rounded-lg bg-[#ff4d97] text-white px-5 py-2.5 font-medium hover:bg-[#e83c84] transition-colors"
            >
              Submit Answers
            </button>
          )}

          {showResults && !passed && (
            <button
              onClick={handleTryAgain}
              className="rounded-lg bg-[#80365c] text-white px-5 py-2.5 font-medium hover:bg-[#6d2d4e] transition-colors"
            >
              noo ur super close, try again!
            </button>
          )}

          {showResults && passed && (
            <button
              onClick={onPassed}
              className="rounded-lg bg-green-600 text-white px-5 py-2.5 font-medium hover:bg-green-700 transition-colors"
            >
              U GOT IT! ur so smart :)
            </button>
          )}

          {showResults && (
            <span className="text-sm text-[#5f4352]">Score: {score}/{QUESTIONS.length}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Quiz;
